import React, { useState } from 'react';
import {
  Typography, Divider, Row, Col, Switch, InputNumber, Input, Space, Tag, Button,
  Modal, ColorPicker,
} from 'antd';
import {
  CheckCircleFilled, ArrowUpOutlined, ArrowDownOutlined, ExpandAltOutlined,
} from '@ant-design/icons';
import SubirImagen from './SubirImagen';
// SE IMPORTAN, no se referencian por una URL de `public/`.
//
// Un archivo de `public/` conserva su nombre tal cual, asi que reemplazarlo
// deja la MISMA URL: el browser y el CDN siguen sirviendo la copia vieja y el
// productor ve el wireframe anterior sin ningun error a la vista. Ya paso —
// estas dos imagenes se actualizaron y en pantalla seguian las de antes.
//
// Importadas, webpack les mete el hash del CONTENIDO en el nombre
// (`op-1.a1b2c3.jpg`), asi que cambiar la imagen cambia la URL y la cache se
// invalida sola. Sin hard refresh, y sin que dependa de que alguien se acuerde.
import opUno from '../../../../assets/plantillas/op-1.jpg';
import opDos from '../../../../assets/plantillas/op-2.jpg';
import { REDES } from '../../../../utils/redesSociales';

const { Title, Text, Paragraph } = Typography;

// Las plantillas del index. Los wireframes viven en `src/assets` y no en
// `static/uploads` como las cabeceras: esa carpeta está en `.gitignore` (vive
// en el volumen del servidor), así que una imagen puesta ahí existe en
// producción y en ninguna otra instalación. Son parte de la interfaz, no
// contenido que sube un usuario, y tienen que viajar con el código.
const PLANTILLAS = [
  {
    value: 'op1',
    titulo: 'OP-1',
    resumen: 'Toros en tarjeta, foto al lado de la ficha. Anda con cualquier foto decente. Franja de bienvenida arriba de la cabecera',
    src: opUno,
  },
  {
    value: 'op2',
    titulo: 'OP-2',
    // LA ADVERTENCIA VA ACÁ, donde se elige: OP-2 pone la foto del toro de
    // fondo a ancho completo con la tarjeta flotando encima. Con fotos
    // verticales o con el animal contra el borde derecho, la tarjeta lo tapa.
    // Elegir OP-2 sin saberlo y publicar así era el modo de falla real.
    resumen: 'Toros a ancho completo, con la foto de fondo, y hembras en un mosaico de dos columnas con los filtros adentro. EXIGE fotos apaisadas: con aire del lado derecho en los toros, donde cae la tarjeta. Barra de bienvenida debajo del hero',
    src: opDos,
  },
];

// Alto FIJO de la miniatura. Son wireframes largos: a tamaño completo, dos
// ocupan la pantalla entera y hay que scrollear para compararlos, que es
// justo lo que uno quiere hacer al elegir.
//
// El recorte lo hace un contenedor con `height` fijo y `overflow: hidden`, NO
// un `maxHeight` sobre la imagen: con maxHeight el contenedor seguía tomando
// el alto natural del <img> y las tarjetas salían enteras igual. Y tampoco un
// porcentaje —lo que se probó primero— porque se resuelve contra una altura
// que sale del propio contenido: referencia circular.
const ALTO_MINIATURA = 190;

// A NIVEL DE MÓDULO, no dentro del tab. Definir un componente durante el render
// hace que React lo vea como uno NUEVO en cada cambio y remonte las imágenes:
// parpadean en cada click. Es la misma razón que explica `ImageOption` en
// `EstructuraTab`, y la misma que vale para `ColorField` en `ColoresTab`.
//
// El click ABRE, no elige: `onVer` monta el modal y la elección se confirma
// adentro, con "Usar esta plantilla". Se hace así para no obligar a un botón de
// lupa por tarjeta, y porque una plantilla se elige mirándola entera, no por una
// miniatura de 120px.
const OpcionPlantilla = ({ opcion, seleccionada, onVer }) => (
  <div
    onClick={() => onVer(opcion)}
    style={{
      cursor: 'pointer',
      border: seleccionada ? '3px solid #1890ff' : '2px solid #e8e8e8',
      borderRadius: 8,
      padding: 8,
      transition: 'all 0.3s',
      backgroundColor: seleccionada ? '#e6f7ff' : '#fff',
      position: 'relative',
    }}
  >
    <div
      style={{
        position: 'relative',
        // `height` fijo, no `maxHeight`: con maxHeight el contenedor tomaba el
        // alto natural del <img> y la tarjeta salía entera igual.
        height: ALTO_MINIATURA,
        overflow: 'hidden',
        borderRadius: 4,
      }}
    >
      <img
        src={opcion.src}
        alt={`Plantilla ${opcion.titulo}`}
        style={{ width: '100%', height: 'auto', display: 'block' }}
      />
      {/* El degradé NO es decoración: sin él, la imagen cortada al ras parece
          una imagen rota y no una que sigue más abajo. */}
      <div
        style={{
          position: 'absolute', left: 0, right: 0, bottom: 0, height: 56,
          background: 'linear-gradient(to bottom, rgba(255,255,255,0), #fff)',
          display: 'flex', alignItems: 'flex-end', justifyContent: 'center',
          paddingBottom: 4,
        }}
      >
        <Text type="secondary" style={{ fontSize: 11 }}>
          <ExpandAltOutlined /> Tocá para verla completa
        </Text>
      </div>
    </div>
    {seleccionada && (
      <CheckCircleFilled
        style={{
          position: 'absolute', top: 16, right: 16, fontSize: 22,
          color: '#1890ff', backgroundColor: '#fff', borderRadius: '50%',
        }}
      />
    )}
    <div style={{ marginTop: 8 }}>
      <Space size={6} wrap>
        <Text strong>{opcion.titulo}</Text>
        {seleccionada && <Tag color="blue">Elegida</Tag>}
      </Space>
      <div>
        <Text type="secondary" style={{ fontSize: 12 }}>{opcion.resumen}</Text>
      </div>
    </div>
  </div>
);


// A NIVEL DE MÓDULO, mismo motivo que `OpcionPlantilla` arriba y que
// `ColorField` en `ColoresTab.jsx` —el mismo patrón de esta pantalla—: un
// `ColorPicker` definido DENTRO del render se remonta en cada `onChange`, que
// dispara en cada movimiento del cursor sobre la paleta, y el popover se
// cierra solo, imposibilitando arrastrar para elegir.
//
// `valor` viaja SEPARADO de `resuelto`: `bienvenida_color` vacío significa
// "usar el de la marca", y el picker tiene que MOSTRAR ese color resuelto sin
// que eso se confunda con haberlo elegido a mano — el que queda escrito en la
// config, al tocar el picker, sigue siendo la clave propia.
//
// Lo usan la barra de bienvenida y el velo de OP-2. `disabledAlpha` porque la
// opacidad NO es del productor: la barra es opaca y el velo trae la suya en
// las paradas del degradado (`veloCabeceraOp2` en `CatalogoPage`). Con alfa,
// el picker devolvía `#rrggbbaa` y la página tenía que recortarlo.
const CampoColor = ({ label, configKey, valor, resuelto, onChange }) => (
  <Col xs={24} sm={12}>
    <Text strong style={{ display: 'block', marginBottom: 4 }}>{label}</Text>
    <ColorPicker
      value={valor || resuelto}
      onChange={(color) => onChange(configKey, color.toHexString())}
      showText
      size="small"
      disabledAlpha
    />
  </Col>
);

// Los de fábrica del velo. DUPLICAN a `DEFAULT_CATALOGO_CONFIG` del backend a
// propósito y sólo para el "Volver a los de fábrica": la config que llega al
// panel ya viene mergeada con los defaults, así que un catálogo nunca los ve
// vacíos; pero para VOLVER hay que saber a qué. Si cambian allá, cambian acá.
const VELO_DE_FABRICA = { op2_velo_on: true, op2_velo_color: '#ffffff', op2_velo_alto: 280 };

/**
 * Lo que es PROPIO del catálogo de genética y no existe en el micrositio de un
 * remate: qué plantilla usa el index, y cómo se arman las dos secciones de
 * animales.
 *
 * Todo lo demás —cabecera, footer, colores, tipografías, hero— se configura con
 * los tabs del micrositio, que se reusan sin tocarlos porque
 * `DEFAULT_CATALOGO_CONFIG` comparte deliberadamente esos nombres de clave con
 * `DEFAULT_MICROSITIO_CONFIG`.
 *
 * @param {string}   plantilla         `plantilla_index` del catalogo.
 * @param {function} onPlantillaChange (valor) => void
 *
 * La plantilla va SEPARADA de `config` y con su propio handler porque es una
 * COLUMNA de `catalogo_genetica`, no una clave del JSON de configuracion.
 * Mandarla por `onChange` la metia dentro de `configuracion_micrositio`, donde
 * `set_config()` la descarta por desconocida — y el picker quedaba eligiendo
 * algo que no se guardaba nunca.
 */
const EstructuraCatalogoTab = ({
  config = {}, onChange, plantilla = 'op1', onPlantillaChange,
  onSubirImagen, onQuitarImagen,
}) => {
  // Qué plantilla se está mirando en grande. El modal se abre al tocar una
  // miniatura y ahí adentro se elige: separar VER de ELEGIR es lo correcto
  // cuando la miniatura no alcanza para decidir, que es justamente el caso.
  const [viendo, setViendo] = useState(null);

  const orden = config.secciones_orden || ['toros', 'hembras'];

  const invertirOrden = () => onChange('secciones_orden', [...orden].reverse());

  // El tag del orden dice el titulo QUE CONFIGURO EL PRODUCTOR, que se edita
  // veinte lineas mas abajo en esta misma pantalla. Con los nombres fijos,
  // renombrar la seccion a 'Padres de la casa' dejaba el tag diciendo 'Toros
  // padres'. Y 'Hembras' ya estaba mal de fabrica: el default del modelo es
  // 'Hembras seleccionadas', asi que mentia en un catalogo recien creado.
  const NOMBRES = {
    toros: config.toros_titulo || 'Toros padres',
    hembras: config.hembras_titulo || 'Hembras seleccionadas',
  };

  return (
    <div>
      {/* ── Plantilla del index ─────────────────────────────────────────── */}
      <Title level={5} style={{ marginBottom: 4 }}>Estructura de la portada</Title>
      <Paragraph type="secondary" style={{ fontSize: 13 }}>
        Las dos muestran los mismos animales y las mismas páginas. Lo que cambia es
        cómo se presentan los toros en la portada y dónde entra la barra de
        bienvenida. OP-2 usa la foto de cada toro como fondo a ancho completo:
        necesita fotos apaisadas, con el animal centrado y aire a la derecha.
      </Paragraph>
      {/* Un tercio cada una y no un cuarto: las opciones son DOS. En col-3
          los wireframes quedan angostos y altos —ilegibles— y media fila
          vacía. Lo que resuelve que no ocupen media pantalla es el recorte,
          no el ancho. */}
      <Row gutter={[16, 16]}>
        {PLANTILLAS.map((p) => (
          <Col xs={24} sm={12} md={8} key={p.value}>
            <OpcionPlantilla
              opcion={p}
              seleccionada={plantilla === p.value}
              onVer={setViendo}
            />
          </Col>
        ))}
      </Row>

      {/* ── El velo de OP-2 ─────────────────────────────────────────────── */}
      {/* SÓLO CON OP-2 ELEGIDA. En OP-1 el título de los toros va en el flujo,
          sin foto detrás, y estas tres claves no las lee nadie: mostrarlas
          sería ofrecer un control que no hace nada, que es peor que no
          ofrecerlo. Se lee `plantilla` (la columna) y no la config, porque es
          la plantilla la que decide si el velo existe. */}
      {plantilla === 'op2' && (
        <div style={{ marginTop: 20 }}>
          <Title level={5} style={{ marginBottom: 4 }}>Velo sobre la foto de los toros</Title>
          <Paragraph type="secondary" style={{ fontSize: 13 }}>
            Un degradado que arranca en este color arriba y se desvanece hacia
            abajo, para que el título de la sección se lea sobre cualquier foto.
            Con una foto clara y el título oscuro podés apagarlo; con una foto
            oscura y el título blanco, probá uno oscuro.
          </Paragraph>
          <Space style={{ marginBottom: 12 }}>
            <Switch
              checked={config.op2_velo_on !== false}
              onChange={(v) => onChange('op2_velo_on', v)}
              checkedChildren="Sí" unCheckedChildren="No"
            />
            <Text strong>Mostrar el velo</Text>
          </Space>
          {config.op2_velo_on !== false && (
            <Row gutter={[16, 12]}>
              <CampoColor
                label="Color"
                configKey="op2_velo_color"
                valor={config.op2_velo_color}
                resuelto={VELO_DE_FABRICA.op2_velo_color}
                onChange={onChange}
              />
              <Col xs={24} sm={12}>
                <Text strong style={{ display: 'block', marginBottom: 4 }}>Hasta dónde baja</Text>
                <Space>
                  {/* El mismo rango que el clamp de `veloCabeceraOp2`: 80 es
                      apenas el título, 620 es el bloque entero. */}
                  <InputNumber
                    min={80} max={620} step={20}
                    addonAfter="px"
                    value={config.op2_velo_alto ?? VELO_DE_FABRICA.op2_velo_alto}
                    onChange={(v) => onChange('op2_velo_alto', v)}
                  />
                  <Text type="secondary" style={{ fontSize: 12 }}>
                    el bloque mide 620
                  </Text>
                </Space>
              </Col>
            </Row>
          )}
          {/* SÓLO SI HAY ALGO DISTINTO DE FÁBRICA, mismo criterio que el link
              de la barra de bienvenida: un "volver" a donde ya se está no
              hace nada y confunde. */}
          {Object.keys(VELO_DE_FABRICA).some(
            (k) => (config[k] ?? VELO_DE_FABRICA[k]) !== VELO_DE_FABRICA[k],
          ) && (
            <Button
              type="link"
              size="small"
              style={{ padding: 0, marginTop: 4 }}
              onClick={() => Object.entries(VELO_DE_FABRICA).forEach(([k, v]) => onChange(k, v))}
            >
              Volver al de fábrica
            </Button>
          )}
        </div>
      )}

      <Divider />

      {/* ── Las dos secciones de animales ───────────────────────────────── */}
      <Title level={5} style={{ marginBottom: 4 }}>Secciones de animales</Title>
      <Paragraph type="secondary" style={{ fontSize: 13 }}>
        El listado completo de cada sección existe SIEMPRE, con las dos plantillas.
        Acá elegís cuántos entran a la portada antes del «Ver todos».
      </Paragraph>

      <div style={{ marginBottom: 12 }}>
        <Space wrap>
          <Text type="secondary" style={{ fontSize: 13 }}>Orden en la página:</Text>
          {orden.map((sec, i) => (
            <Tag key={sec} color="blue">{i + 1}. {NOMBRES[sec] || sec}</Tag>
          ))}
          <Button type="link" size="small" onClick={invertirOrden} style={{ padding: 0 }}>
            <ArrowUpOutlined /><ArrowDownOutlined /> Invertir
          </Button>
        </Space>
      </div>

      <Row gutter={[24, 16]}>
        <Col xs={24} md={12}>
          <Space direction="vertical" size={10} style={{ width: '100%' }}>
            <Space>
              <Switch
                checked={config.toros_on !== false}
                onChange={(v) => onChange('toros_on', v)}
                checkedChildren="Sí" unCheckedChildren="No"
              />
              <Text strong>Mostrar toros padres</Text>
            </Space>
            <Input
              addonBefore="Título"
              value={config.toros_titulo ?? 'Toros padres'}
              onChange={(e) => onChange('toros_titulo', e.target.value)}
            />
            <Input
              addonBefore="Bajada"
              placeholder="Opcional"
              value={config.toros_subtitulo || ''}
              onChange={(e) => onChange('toros_subtitulo', e.target.value)}
            />
            <Space>
              <Text>En la portada:</Text>
              <InputNumber
                min={1} max={24}
                value={config.toros_destacados_n ?? 6}
                onChange={(v) => onChange('toros_destacados_n', v)}
              />
              <Text type="secondary" style={{ fontSize: 12 }}>
                los primeros del orden que armaste
              </Text>
            </Space>
          </Space>
        </Col>

        <Col xs={24} md={12}>
          <Space direction="vertical" size={10} style={{ width: '100%' }}>
            <Space>
              <Switch
                checked={config.hembras_on !== false}
                onChange={(v) => onChange('hembras_on', v)}
                checkedChildren="Sí" unCheckedChildren="No"
              />
              <Text strong>Mostrar hembras</Text>
            </Space>
            <Input
              addonBefore="Título"
              value={config.hembras_titulo ?? 'Hembras seleccionadas'}
              onChange={(e) => onChange('hembras_titulo', e.target.value)}
            />
            <Input
              addonBefore="Bajada"
              placeholder="Opcional"
              value={config.hembras_subtitulo || ''}
              onChange={(e) => onChange('hembras_subtitulo', e.target.value)}
            />
            <Space>
              <Text>En la portada:</Text>
              <InputNumber
                min={1} max={24}
                value={config.hembras_destacados_n ?? 6}
                onChange={(v) => onChange('hembras_destacados_n', v)}
              />
            </Space>
            <Space>
              <Switch
                size="small"
                checked={config.hembras_filtros_on !== false}
                onChange={(v) => onChange('hembras_filtros_on', v)}
              />
              <Text type="secondary" style={{ fontSize: 13 }}>
                Filtros por raza y categoría dentro de la sección de hembras (por ahora, en OP-2)
              </Text>
            </Space>
          </Space>
        </Col>
      </Row>

      <Divider />

      {/* ── Logo ────────────────────────────────────────────────────────── */}
      <Title level={5} style={{ marginBottom: 4 }}>Logo de la cabecera</Title>
      <Paragraph type="secondary" style={{ fontSize: 13 }}>
        El tamaño se ajusta más abajo, en «Cabecera y pie». Si no subís ninguno,
        se usa el logo de tu identidad visual — el mismo que sale en los remates.
      </Paragraph>
      <SubirImagen
        valor={config.logo_img}
        campo="logo_img"
        onSubir={onSubirImagen}
        onQuitar={onQuitarImagen}
        alto={56}
        ayuda="PNG con fondo transparente es lo que mejor queda sobre la foto del hero."
      />

      <Divider />

      {/* ── Imagen sobre el hero ────────────────────────────────────────── */}
      <Title level={5} style={{ marginBottom: 4 }}>Imagen sobre el hero</Title>
      <Paragraph type="secondary" style={{ fontSize: 13 }}>
        Se apoya ENCIMA de la foto de fondo, sin recortarse ni deformarse: sirve
        para un logo grande, un sello de aniversario o la marca de la cabaña. La
        foto de fondo se configura en «Hero Zone».
      </Paragraph>
      <SubirImagen
        valor={config.hero_overlay_img}
        campo="hero_overlay_img"
        onSubir={onSubirImagen}
        onQuitar={onQuitarImagen}
        alto={72}
        ayuda="Usá PNG con transparencia: un JPG va a tapar la foto con su fondo."
      />
      {config.hero_overlay_img && (
        <Row gutter={[16, 12]} style={{ marginTop: 12 }}>
          <Col xs={24} md={12}>
            <Space>
              <Text>Ancho máximo</Text>
              <InputNumber
                min={80} max={900} step={20}
                addonAfter="px"
                value={config.hero_overlay_ancho ?? 320}
                onChange={(v) => onChange('hero_overlay_ancho', v)}
              />
            </Space>
          </Col>
          <Col xs={24} md={12}>
            <Space>
              <Text>Posición</Text>
              {['izquierda', 'centro', 'derecha'].map((p) => (
                <Tag
                  key={p}
                  color={(config.hero_overlay_pos || 'centro') === p ? 'blue' : 'default'}
                  style={{ cursor: 'pointer', textTransform: 'capitalize' }}
                  onClick={() => onChange('hero_overlay_pos', p)}
                >
                  {p}
                </Tag>
              ))}
            </Space>
          </Col>
        </Row>
      )}

      <Divider />

      {/* ── Cabecera y pie ──────────────────────────────────────────────── */}
      <Title level={5} style={{ marginBottom: 4 }}>Cabecera y pie de página</Title>
      <Paragraph type="secondary" style={{ fontSize: 13 }}>
        Apagalos si vas a incrustar el catálogo dentro de tu sitio: esa página ya
        pone su propia cabecera y su propio pie, y dejarlos prendidos deja dos
        menús, uno adentro del otro.
      </Paragraph>
      <Space direction="vertical" size={10} style={{ marginBottom: 8 }}>
        <Space>
          <Switch
            checked={config.cabecera_on !== false}
            onChange={(v) => onChange('cabecera_on', v)}
            checkedChildren="Sí" unCheckedChildren="No"
          />
          <Text>Mostrar cabecera</Text>
        </Space>
        <Space>
          <Switch
            checked={config.footer_on !== false}
            onChange={(v) => onChange('footer_on', v)}
            checkedChildren="Sí" unCheckedChildren="No"
          />
          <Text>Mostrar pie de página</Text>
        </Space>
        {(config.cabecera_on === false || config.footer_on === false) && (
          // El aviso importa: los switches valen TAMBIEN para el link directo,
          // no sólo para el sitio incrustado. Sin cabecera, el que entra por la
          // URL que le pasaste por WhatsApp no tiene con qué navegar.
          <Text type="warning" style={{ fontSize: 12 }}>
            Ojo: también se apagan en el link directo a tu catálogo, no sólo
            cuando lo incrustás.
          </Text>
        )}
      </Space>

      <Divider />

      {/* ── Bienvenida ──────────────────────────────────────────────────── */}
      <Title level={5} style={{ marginBottom: 4 }}>Barra de bienvenida</Title>
      <Paragraph type="secondary" style={{ fontSize: 13 }}>
        La ubicás con la plantilla de arriba: en OP-1 es una franja fina arriba
        de la cabecera, fija con ella, en las TRES páginas del catálogo —
        portada, ficha y listado—. En OP-2 es una barra más alta debajo del
        hero, sólo en la portada. El mensaje, el link y los colores son los
        mismos en las dos.
      </Paragraph>
      <Input.TextArea
        rows={2}
        placeholder="Ej: Genética Braford con 40 años de selección en el norte santafesino"
        value={config.mensaje_bienvenida || ''}
        onChange={(e) => onChange('mensaje_bienvenida', e.target.value)}
      />

      <div style={{ marginTop: 12 }}>
        <Input
          addonBefore="Link (opcional)"
          placeholder="https://..."
          value={config.bienvenida_link || ''}
          onChange={(e) => onChange('bienvenida_link', e.target.value)}
        />
        <Text type="secondary" style={{ fontSize: 12 }}>
          Si lo cargás, toda la barra lleva ahí.
        </Text>
      </div>

      <Row gutter={16} style={{ marginTop: 12 }}>
        <CampoColor
          label="Fondo"
          configKey="bienvenida_color"
          valor={config.bienvenida_color}
          resuelto={config.color_primary || '#1a5f2a'}
          onChange={onChange}
        />
        <CampoColor
          label="Texto"
          configKey="bienvenida_color_texto"
          valor={config.bienvenida_color_texto}
          resuelto="#ffffff"
          onChange={onChange}
        />
      </Row>
      {/* SÓLO SI HAY ALGO PROPIO CARGADO: sin esto el link queda siempre a la
          vista, prometiendo "volver" a un estado en el que ya está. Vacío en
          las dos claves ES el estado de fábrica —el fondo sale de
          `color_primary` y el texto blanco— así que limpiar cuando ya están
          vacías no haría nada. */}
      {(config.bienvenida_color || config.bienvenida_color_texto) && (
        <Button
          type="link"
          size="small"
          style={{ padding: 0, marginTop: 4 }}
          onClick={() => {
            onChange('bienvenida_color', '');
            onChange('bienvenida_color_texto', '');
          }}
        >
          Usar los de la marca
        </Button>
      )}

      <div style={{ marginTop: 20 }}>
        <Title level={5} style={{ marginBottom: 4 }}>Redes en la barra</Title>
        <Paragraph type="secondary" style={{ fontSize: 13 }}>
          Se muestran a la derecha del mensaje. Pegá el usuario o el link del perfil.
        </Paragraph>
        <Row gutter={[16, 12]}>
          {REDES.map((r) => (
            <Col key={r.key} xs={24} md={12}>
              <Input
                prefix={React.cloneElement(r.icon, { style: { color: r.color } })}
                placeholder={`Usuario o link de ${r.label}`}
                value={config.redes?.[r.key] || ''}
                onChange={(e) => onChange('redes', { ...(config.redes || {}), [r.key]: e.target.value })}
              />
            </Col>
          ))}
        </Row>
      </div>

      {/* La vista completa. El scroll vive ACA adentro y no en la pantalla:
          estas imágenes son largas, y dejarlas crecer en la grilla obligaba a
          scrollear toda la configuración para volver a los controles. */}
      <Modal
        open={Boolean(viendo)}
        onCancel={() => setViendo(null)}
        title={viendo ? viendo.titulo + ' — ' + viendo.resumen : ''}
        width={900}
        styles={{ body: { maxHeight: '70vh', overflowY: 'auto', padding: 0 } }}
        footer={[
          <Button key="cerrar" onClick={() => setViendo(null)}>Cerrar</Button>,
          <Button
            key="elegir"
            type="primary"
            disabled={viendo ? plantilla === viendo.value : true}
            onClick={() => { onPlantillaChange(viendo.value); setViendo(null); }}
          >
            {viendo && plantilla === viendo.value
              ? 'Ya es la elegida'
              : 'Usar esta plantilla'}
          </Button>,
        ]}
      >
        {viendo && (
          <img
            src={viendo.src}
            alt={'Plantilla ' + viendo.titulo}
            style={{ width: '100%', height: 'auto', display: 'block' }}
          />
        )}
      </Modal>
    </div>
  );
};

export default EstructuraCatalogoTab;
